import './PostPlaceholder.sass';

import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router5';

import { SubscriptionPlaceholderProps } from './PostPlaceholder.interface';
import { Button, Placeholder } from '$uikit';
import { SubscriptionIcon, UnitName } from '$shared/components';
import { AttachType, PostType, SubscriptionType } from '$store/models';
import { classNames as cn } from '$utils';
import { authSelector } from '$store/auth';
import { useCanViewPost, useTranslator, useTransliterateName, useUserInfo } from '$hooks';
import {
  INVITE,
  MAIN,
  SUBSCRIPTION_ADULT,
  SUBSCRIPTION_AUTHOR,
  SUBSCRIPTION_MEDIA,
} from '$shared/constants/pages';

export const PostPlaceholder: FC<SubscriptionPlaceholderProps> = ({ post, children }) => {
  const { isLogged } = useSelector(authSelector);
  const user = useUserInfo(post.ownerId);
  const t = useTranslator();
  const canView = useCanViewPost(post);
  const userName = useTransliterateName(user ? user.firstName + ' ' + user.lastName : '');

  const containsRepost = useMemo(() => {
    return (
      post?.attachments &&
      post.attachments.filter((a) => a.type === AttachType.LINK).length > 0
    );
  }, []);

  if (containsRepost || canView) {
    return <>{children}</>;
  }

  function renderContent() {
    if (isLogged) {
      if (post.type === PostType.PRIVATE_SUBSCRIPTION) {
        return (
          <Placeholder
            icon={
              <SubscriptionIcon type={SubscriptionType.Author} authorId={post.ownerId} />
            }
            title={<UnitName unit={user} />}
            actions={
              <Link
                routeName={SUBSCRIPTION_AUTHOR}
                routeParams={{ authorId: post.ownerId }}
              >
                <Button>{t('blur_private_subscription_button')}</Button>
              </Link>
            }
          >
            {t('blur_private_subscription_label')}
          </Placeholder>
        );
      }
      if (post.ownerId < 0 || user.verified !== 2) {
        return (
          <Placeholder
            icon={
              <SubscriptionIcon type={SubscriptionType.Media} authorId={post.ownerId} />
            }
            title={t('subscription_type_media')}
            actions={
              <Link routeName={SUBSCRIPTION_MEDIA}>
                <Button>{t('blur_subscription_button')}</Button>
              </Link>
            }
          >
            {t('blur_subscription_label')}
          </Placeholder>
        );
      } else {
        return (
          <Placeholder
            icon={
              <SubscriptionIcon type={SubscriptionType.Adult} authorId={post.ownerId} />
            }
            title={t('subscription_type_adult')}
            actions={
              <Link routeName={SUBSCRIPTION_ADULT}>
                <Button>{t('blur_adult_subscription_button')}</Button>
              </Link>
            }
          >
            {t('blur_adult_subscription_label')}
          </Placeholder>
        );
      }
    } else {
      return (
        <Placeholder
          actions={
            <Link routeName={MAIN}>
              <Button size="small">{t('blur_not_logged_button')}</Button>
            </Link>
          }
        >
          {t('blur_not_logged')}
        </Placeholder>
      );
    }
  }

  return (
    <div
      className={cn('PostPlaceholder', {
        private: isLogged && post.type === PostType.PRIVATE_SUBSCRIPTION,
        media:
          isLogged &&
          post.type === PostType.SUBSCRIPTION &&
          (post.ownerId < 0 || user.verified === 1),
        adult:
          isLogged && post.type === PostType.SUBSCRIPTION && user && user.verified === 2,
        empty: !post.attachments?.length,
      })}
    >
      {children}
      <div className="PostPlaceholder__content">{renderContent()}</div>
    </div>
  );
};
